import type { ErrorItem } from '@/types';

import React from 'react';
import { Chip, Tooltip } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import { errorzListParamsAtom, errorzQueryAtom } from '@/store';

// Custom hooks
import { useErrorzFilters, useErrorzSorting } from '@/hooks/errorz';
import { useVisibleColumns } from '@/hooks/table';

// Components
import { SearchFieldsComponent } from '@/components/errorz/SearchFields';
import { UserAgentChips } from '@/components/shared/UserAgentChips';
import { FingerprintChip } from '@/components/shared/FingerprintChip';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { JsonViewer, JsonViewerPopover } from '@/components/shared/JsonViewer';

export default function ErrorzPage() {
  const { data, isLoading, isError, error } = useAtomValue(errorzQueryAtom);
  const [params, setParams] = useAtom(errorzListParamsAtom);

  const errorz = React.useMemo<ErrorItem[]>(() => data?.data ?? [], [data]);

  // Custom hooks
  const {
    stageFilter,
    statusFilter,
    searchFields,
    setSearchFields,
    isSearchOpen,
    setIsSearchOpen,
    hasAnyFilter,
    applyFilters,
    clearFilters,
    handleStageChange,
    handleStatusChange,
    handlePageChange
  } = useErrorzFilters();

  const { sortDescriptor, setSortDescriptor, sortedErrors } = useErrorzSorting(errorz);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;

    return text.substring(0, maxLength) + '...';
  };

  const renderCell = React.useCallback((errorItem: ErrorItem, columnKey: React.Key) => {
    switch (columnKey) {
      case 'message':
        return <span>{truncateText(errorItem.message, 40)}</span>;
      case 'stage':
        return (
          <Chip color="primary" size="sm" variant="flat">
            {errorItem.stage}
          </Chip>
        );
      case 'status': {
        const statusColors: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
          raw: 'warning',
          processed: 'success',
          failed: 'danger'
        };
        const color = statusColors[errorItem.status] || 'default';

        return (
          <Chip color={color} size="sm" variant="flat">
            {errorItem.status}
          </Chip>
        );
      }
      case 'ip':
        return errorItem.ip;
      case 'user_agent':
        return <UserAgentChips userAgent={errorItem.user_agent} />;
      case 'fingerprint':
        return <FingerprintChip fingerprint={errorItem.fingerprint} />;
      case 'error':
        return <JsonViewerPopover data={errorItem.error} title="Error Details" />;
      case 'metadata':
        return errorItem.metadata ? (
          <JsonViewerPopover data={errorItem.metadata} title="Metadata" />
        ) : (
          <span className="text-xs text-gray-400">No metadata</span>
        );
      case 'created_at':
        return <DateTimeDisplay dateString={errorItem.created_at} showSeconds={true} />;
      default:
        return null;
    }
  }, []);

  const renderCollapseContent = React.useCallback((errorItem: ErrorItem) => {
    return (
      <div className="mb-4 space-y-6">
        <JsonViewer
          data={errorItem.error}
          maxHeightPx={320}
          unstyled
          className="border-default-200 overflow-auto rounded border bg-black/50 p-3 text-xs"
        />
        {errorItem.error && (
          <div>
            <JsonViewerPopover data={errorItem.error} title="View JSON" />
          </div>
        )}

        {errorItem.metadata && (
          <div>
            <h4 className="mb-2 text-sm font-semibold">Metadata</h4>
            <JsonViewer
              data={errorItem.metadata}
              maxHeightPx={200}
              unstyled
              className="border-default-200 overflow-auto rounded border bg-black/50 p-3 text-xs"
            />
          </div>
        )}

        <div>
          <h4 className="mb-2 text-sm font-semibold">User Agent</h4>
          <div className="space-y-2">
            <UserAgentChips userAgent={errorItem.user_agent} />
            <div className="max-h-32 overflow-auto rounded bg-gray-100 p-3 text-xs dark:bg-gray-800">
              {errorItem.user_agent}
            </div>
          </div>
        </div>

        {errorItem.fingerprint && (
          <div>
            <h4 className="mb-2 text-sm font-semibold">Fingerprint</h4>
            <FingerprintChip size="lg" maxLength={250} fingerprint={errorItem.fingerprint} />
          </div>
        )}
      </div>
    );
  }, []);

  const columns = React.useMemo(
    () => [
      { name: 'Message', uid: 'message', sortable: true },
      { name: 'Stage', uid: 'stage', sortable: true },
      { name: 'Status', uid: 'status', sortable: true },
      // Removed IP, UA and Fingerprint from main table; shown in expanded view
      { name: 'Error Details', uid: 'error', sortable: false },
      { name: 'Metadata', uid: 'metadata', sortable: false },
      { name: 'Created At', uid: 'created_at', sortable: true, align: 'end', cellClass: 'text-right' }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns, [
    'message',
    'stage',
    'status',
    'created_at'
  ]);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedErrors}
      emptyMessage="No errorz found"
      error={error}
      errorMessage="Failed to load errorz"
      enableCollapse={true}
      renderCollapseContent={renderCollapseContent}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchFieldsComponent
            searchFields={searchFields}
            setSearchFields={setSearchFields}
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            onApply={applyFilters}
            onClear={clearFilters}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <FilterSelect
            label="Stage"
            value={stageFilter}
            options={[
              { key: 'all', label: 'All' },
              { key: 'script', label: 'Script' },
              { key: 'api', label: 'API' },
              { key: 'server', label: 'Server' }
            ]}
            onChange={handleStageChange}
          />

          <FilterSelect
            label="Status"
            value={statusFilter}
            options={[
              { key: 'all', label: 'All' },
              { key: 'raw', label: 'Raw' },
              { key: 'processed', label: 'Processed' },
              { key: 'failed', label: 'Failed' }
            ]}
            onChange={handleStatusChange}
          />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title="Errorz"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) => setSortDescriptor(d)}
    />
  );
}
